import { useState } from 'react';
import { useAppStore } from '../store';
import { motion } from 'framer-motion';
import { Lightbulb, ChevronDown, Target, BookOpen, Zap, ArrowRight } from 'lucide-react';

export default function DifficultyPage() {
  const { difficultyBreakdowns, units, activeSubjectId } = useAppStore();
  const [expanded, setExpanded] = useState<string>(difficultyBreakdowns[0]?.id || '');

  return (
    <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">难点解答</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">难点逐步拆解，提供理解技巧与 IB 答题思路</p>
      </motion.div>

      <div className="space-y-4">
        {difficultyBreakdowns.map((db, idx) => {
          const isOpen = expanded === db.id;
          return (
            <motion.div
              key={db.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.06 }}
              className="card-premium overflow-hidden"
            >
              <button
                onClick={() => setExpanded(isOpen ? '' : db.id)}
                className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-linen/50 transition-colors active:bg-linen/30 touch-target"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cedar to-cedar-light flex items-center justify-center shadow-sm">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-stone-950 font-body">{db.title}</h3>
                    <p className="text-[10px] text-stone-400 font-body">{db.breakdown.length} 步拆解</p>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-4 sm:space-y-5">
                  <div>
                    <h4 className="text-xs font-semibold text-stone-950 mb-3 flex items-center gap-2 font-body">
                      <Target className="w-3.5 h-3.5 text-cedar" /> 难点拆解
                    </h4>
                    <div className="space-y-2">
                      {db.breakdown.map((step, i) => (
                        <div key={i} className="flex items-start gap-3 p-3.5 rounded-xl bg-cedar-50">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cedar/15 text-cedar flex items-center justify-center text-[10px] font-bold font-body">{i + 1}</span>
                          <p className="text-xs text-stone-600 leading-relaxed pt-0.5 font-body">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-stone-950 mb-3 flex items-center gap-2 font-body">
                      <Zap className="w-3.5 h-3.5 text-bronze" /> 理解技巧
                    </h4>
                    <div className="space-y-1.5">
                      {db.understandingTips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-stone-600 bg-linen rounded-xl p-3.5 font-body">
                          <span className="text-bronze mt-0.5">💡</span>
                          {tip}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl p-5 border border-steel-light/30" style={{ background: 'linear-gradient(135deg, #eef3f8, #f2f6fa)' }}>
                    <h4 className="text-xs font-semibold text-steel mb-2.5 flex items-center gap-2 font-body">
                      <BookOpen className="w-3.5 h-3.5" /> IB 答题思路
                    </h4>
                    <p className="text-xs text-stone-600 leading-relaxed font-body">{db.ibAnswerStrategy}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-stone-950 mb-2.5 font-body">练习示例</h4>
                    <div className="space-y-2">
                      {db.examples.map((ex, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-stone-500 p-2.5 rounded-xl bg-linen font-body">
                          <ArrowRight className="w-3 h-3 text-sage flex-shrink-0" />
                          {ex}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
